import React from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import { BookOpen, Target, Lightbulb, Calculator, Users, TrendingUp } from 'lucide-react';

export const TheoryTab: React.FC = () => {
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <BookOpen className="w-8 h-8 text-primary-400 mr-3" />
          <h1 className="text-3xl font-bold text-gray-100">Queueing Theory: Key Concepts</h1>
        </div>
        <p className="text-gray-400 max-w-3xl mx-auto">
          A comprehensive guide to understanding queueing systems, performance metrics, and variability analysis. 
          These concepts form the foundation for analyzing and optimizing service systems.
        </p>
      </div>

      {/* Learning Objectives */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <div className="flex items-center mb-4">
          <Target className="w-6 h-6 text-primary-400 mr-3" />
          <h2 className="text-xl font-semibold text-gray-200">Learning Objectives</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Understand queueing systems as processes
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Learn to measure process performance
            </li>
          </ul>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Identify sources of process variability
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Quantify variability impact on queue performance
            </li>
          </ul>
        </div>
      </div>

      {/* Simple Process */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4 flex items-center">
          <TrendingUp className="w-6 h-6 text-accent-400 mr-3" />
          Simple Process Concept
        </h2>
        <p className="text-gray-300 mb-4">
          Processes convert inputs into outputs. The items or entities processed can be items, people, jobs, financial units, etc.
          According to Wallace Hopp (2011): <em>"A supply chain is a goal-oriented network of processes and stock points used to deliver goods and services to customers."</em>
        </p>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
          <div className="text-center text-gray-300">
            <div className="flex items-center justify-center space-x-8">
              <div className="bg-blue-900/30 px-4 py-2 rounded-lg border border-blue-700">Input</div>
              <div className="flex items-center">
                <div className="w-12 h-0.5 bg-gray-500"></div>
                <div className="w-3 h-3 border-r-2 border-t-2 border-gray-500 rotate-45 ml-2"></div>
              </div>
              <div className="bg-green-900/30 px-4 py-2 rounded-lg border border-green-700">Process</div>
              <div className="flex items-center">
                <div className="w-12 h-0.5 bg-gray-500"></div>
                <div className="w-3 h-3 border-r-2 border-t-2 border-gray-500 rotate-45 ml-2"></div>
              </div>
              <div className="bg-purple-900/30 px-4 py-2 rounded-lg border border-purple-700">Output</div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Performance Measures */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4 flex items-center">
          <Calculator className="w-6 h-6 text-primary-400 mr-3" />
          Key Process Performance Measures
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-primary-400 mb-2">Throughput (TH)</h4>
              <p className="text-gray-300 text-sm">Rate at which items are processed by the system (items/time)</p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-primary-400 mb-2">Work in Process (WIP)</h4>
              <p className="text-gray-300 text-sm">Number of items in the system (items)</p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-primary-400 mb-2">Cycle Time (CT)</h4>
              <p className="text-gray-300 text-sm">Time required for an item to traverse the system (time)</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-accent-400 mb-2">Capacity</h4>
              <p className="text-gray-300 text-sm">Maximum average rate at which items can flow through the system</p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-accent-400 mb-2">Utilization</h4>
              <p className="text-gray-300 text-sm">Rate of flow into a process divided by its capacity</p>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 border border-dark-600">
              <h4 className="font-semibold text-accent-400 mb-2">Bottleneck</h4>
              <p className="text-gray-300 text-sm">The process in a system with the highest utilization</p>
            </div>
          </div>
        </div>
      </div>

      {/* Little's Law */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Little's Law</h2>
        <p className="text-gray-300 mb-4">
          Little's Law states that the long-term average of WIP is equal to the long-term average of the Throughput multiplied by the long-term average Cycle Time.
        </p>
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-600">
          <div className="text-center">
            <BlockMath math="WIP = TH \times CT" />
            <div className="mt-2">
              <InlineMath math="(items) = (items/time) \times (time)" />
            </div>
          </div>
        </div>
      </div>

      {/* Variability */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Impact of Variability</h2>
        <p className="text-gray-300 mb-4">
          Variability within a process occurs due to either arrival and/or process variability. 
          To describe this variability, we use the coefficient of variation:
        </p>
        <div className="bg-dark-900 rounded-lg p-4 border border-dark-600 mb-4">
          <div className="text-center">
            <BlockMath math="CV = \frac{\sigma}{\mu}" />
            <p className="text-sm text-gray-400 mt-2">Coefficient of Variation = Standard Deviation / Mean</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
            <h4 className="font-semibold text-green-400 mb-2">Low Variability</h4>
            <div className="text-center">
              <InlineMath math="0 \leq CV \leq 0.75" />
            </div>
          </div>
          <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-400 mb-2">Moderate Variability</h4>
            <div className="text-center">
              <InlineMath math="0.75 \leq CV \leq 1.33" />
            </div>
          </div>
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
            <h4 className="font-semibold text-red-400 mb-2">High Variability</h4>
            <div className="text-center">
              <InlineMath math="CV > 1.33" />
            </div>
          </div>
        </div>
      </div>

      {/* Queueing Theory */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4 flex items-center">
          <Users className="w-6 h-6 text-primary-400 mr-3" />
          Queueing Theory
        </h2>
        <p className="text-gray-300 mb-6">
          Queueing theory allows us to quantify how variability impacts the performance of a queue. 
          The system consists of an arrival process, a service process, and a queue of entities waiting to be serviced.
        </p>

        {/* Nomenclature */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">Nomenclature</h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-dark-900 rounded-lg border border-dark-600">
              <thead>
                <tr className="border-b border-dark-600">
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Variable</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Definition</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Unit</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="r_a" /></td>
                  <td className="px-4 py-3">Rate of arrivals</td>
                  <td className="px-4 py-3">items/time</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="t_a" /></td>
                  <td className="px-4 py-3">Mean time between arrivals = <InlineMath math="1/r_a" /></td>
                  <td className="px-4 py-3">time/item</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="CV_a" /></td>
                  <td className="px-4 py-3">Coefficient of variation of inter arrivals</td>
                  <td className="px-4 py-3">-</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="m" /></td>
                  <td className="px-4 py-3">Number of parallel servers</td>
                  <td className="px-4 py-3">-</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="r_p" /></td>
                  <td className="px-4 py-3">Rate or capacity of work station</td>
                  <td className="px-4 py-3">items/time</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="t_p" /></td>
                  <td className="px-4 py-3">Mean effective process time = <InlineMath math="m/r_p" /></td>
                  <td className="px-4 py-3">time/item</td>
                </tr>
                <tr>
                  <td className="px-4 py-3"><InlineMath math="CV_p" /></td>
                  <td className="px-4 py-3">Coefficient of variation of process time</td>
                  <td className="px-4 py-3">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">Performance Metrics</h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-dark-900 rounded-lg border border-dark-600">
              <thead>
                <tr className="border-b border-dark-600">
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Metric</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Definition</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Unit</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="t_q" /></td>
                  <td className="px-4 py-3">Expected waiting time in the queue</td>
                  <td className="px-4 py-3">time</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="CT" /></td>
                  <td className="px-4 py-3">Expected time in system = <InlineMath math="t_q + t_p" /></td>
                  <td className="px-4 py-3">time</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="WIP" /></td>
                  <td className="px-4 py-3">Average work in process at station</td>
                  <td className="px-4 py-3">items</td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3"><InlineMath math="WIP_q" /></td>
                  <td className="px-4 py-3">Average work in process in queue</td>
                  <td className="px-4 py-3">items</td>
                </tr>
                <tr>
                  <td className="px-4 py-3"><InlineMath math="u" /></td>
                  <td className="px-4 py-3">Utilization of the server = <InlineMath math="r_a/r_p" /></td>
                  <td className="px-4 py-3">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Kendall's Notation */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">Kendall's Notation (X/Y/m/b)</h3>
          <div className="overflow-x-auto">
            <table className="w-full bg-dark-900 rounded-lg border border-dark-600">
              <thead>
                <tr className="border-b border-dark-600">
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Symbol</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Definition</th>
                  <th className="px-4 py-3 text-left text-gray-300 font-semibold">Type</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3 font-mono">X</td>
                  <td className="px-4 py-3">Distribution of the interarrival times</td>
                  <td className="px-4 py-3 text-sm">
                    D = Deterministic<br/>
                    M = Exponential<br/>
                    G = General time
                  </td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3 font-mono">Y</td>
                  <td className="px-4 py-3">Distribution of the effective process times</td>
                  <td className="px-4 py-3"></td>
                </tr>
                <tr className="border-b border-dark-700">
                  <td className="px-4 py-3 font-mono">m</td>
                  <td className="px-4 py-3">Number of servers at the station</td>
                  <td className="px-4 py-3">items</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">b</td>
                  <td className="px-4 py-3">System capacity at any one time</td>
                  <td className="px-4 py-3">items</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* VUT Equations */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">VUT Equations</h2>
        <p className="text-gray-300 mb-6">
          Kingman's or VUT equation includes three terms: the V (variation), the U (utilization) and the T (processing time).
        </p>

        {/* General Case */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">General Case: G/G/m/∞ Queue</h3>
          <div className="bg-dark-900 rounded-lg p-6 border border-dark-600">
            <div className="text-center">
              <BlockMath math="t_q = \left(\frac{CV_a^2 + CV_p^2}{2}\right) \left(\frac{u^{\sqrt{2(m+1)}-1}}{m(1-u)}\right) t_p" />
            </div>
          </div>
        </div>

        {/* Single Server */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-200 mb-3">Single Server: G/G/1/∞ Queue</h3>
          <div className="bg-dark-900 rounded-lg p-6 border border-dark-600">
            <div className="text-center">
              <BlockMath math="t_q = \left(\frac{CV_a^2 + CV_p^2}{2}\right) \left(\frac{u}{1-u}\right) t_p" />
            </div>
          </div>
        </div>

        {/* Special Cases */}
        <div>
          <h3 className="text-lg font-semibold text-gray-200 mb-4">Special Cases for Single Server (m=1)</h3>
          
          {/* M/M/1 */}
          <div className="mb-4 bg-dark-900 rounded-lg p-6 border border-dark-600">
            <h4 className="font-semibold text-primary-400 mb-3">M/M/1: Exponential arrivals (CV_a=1) and exponential processing (CV_p=1)</h4>
            <div className="space-y-3">
              <div className="text-center">
                <BlockMath math="t_q = \left(\frac{u}{1-u}\right) t_p" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                <div>
                  <BlockMath math="WIP = \frac{u}{1-u}" />
                </div>
                <div>
                  <BlockMath math="CT = \frac{t_p}{1-u}" />
                </div>
              </div>
              <div className="text-center">
                <BlockMath math="WIP_q = \frac{u^2}{1-u}" />
              </div>
            </div>
          </div>

          {/* M/D/1 */}
          <div className="mb-4 bg-dark-900 rounded-lg p-6 border border-dark-600">
            <h4 className="font-semibold text-accent-400 mb-3">M/D/1: Exponential arrivals (CV_a=1) and deterministic processing (CV_p=0)</h4>
            <div className="text-center">
              <BlockMath math="t_q = \left(\frac{1}{2}\right) \left(\frac{u}{1-u}\right) t_p" />
            </div>
          </div>

          {/* D/D/1 */}
          <div className="bg-dark-900 rounded-lg p-6 border border-dark-600">
            <h4 className="font-semibold text-green-400 mb-3">D/D/1: Deterministic arrivals (CV_a=0) and deterministic processing (CV_p=0)</h4>
            <div className="text-center">
              <BlockMath math="t_q = 0" />
            </div>
            <p className="text-sm text-gray-400 text-center mt-2">No waiting time with completely deterministic system</p>
          </div>
        </div>
      </div>

      {/* Multiple Servers */}
      <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-gray-200 mb-4">Multiple Servers</h2>
        <p className="text-gray-300 mb-4">
          We can have: <strong>i)</strong> separate queues with dedicated servers or <strong>ii)</strong> parallel servers with a single queue. 
        </p>
        <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <Lightbulb className="w-5 h-5 text-green-400 mr-2" />
            <h4 className="font-semibold text-green-400">Key Insight</h4>
          </div>
          <p className="text-gray-300">
            Parallel servers with a single queue always outperform dedicated ones, mainly due to the <strong>pooling effect</strong>.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pt-8 border-t border-dark-700">
        <p className="text-gray-400 text-sm">
          Based on MIT MicroMasters in Supply Chain Management materials
        </p>
        <p className="text-gray-500 text-xs mt-2">
          MIT Center for Transportation & Logistics
        </p>
      </div>
    </div>
  );
};